<template>
  <v-data-table
    :headers="headers"
    :items="filteredProducts"
    multi-sort
    item-key="name"
    :expanded.sync="expanded"
    :single-expand="singleExpand"
    :items-per-page="50"
    :footer-props="{ itemsPerPageOptions: [10, 20, 50] }"
    :options.sync="options"
  >
    <template v-slot:header.maximum_per_client="{ header }">
      <mf-tooltip top max-width="192px" :disabled="!header.tooltip">
        <template v-slot:activator="{ on }">
          {{ header.text }}
          <mf-icon v-show="header.tooltip" small color="grey" v-on="on">info</mf-icon>
        </template>
        <span>{{ header.tooltip }}</span>
      </mf-tooltip>
    </template>

    <!-- group content -->
    <template v-if="type === 'grouped' && !isRevision" v-slot:body.prepend>
      <tr>
        <td></td>
        <td></td>
        <td></td>
        <td></td>
        <td>
          <field-toggle
            v-if="!isRevision && !disabled"
            v-model="group.name"
            :error="getGroupValidationError(groupIndex, 'name') || group.name === ''"
            :text="true"
            :column="0"
            :row="groupIndex + 9999"
            :tooltip="isDisabledInRules(rules.takepayOfferSales.name)"
            :tooltip-text="tooltipMessage"
            :is-industry-campaign="isIndustryCampaign"
            :disabled="isDisabledInRules(rules.takepayOfferSales.name)"
          />
          <span v-else>{{ group.name }}</span>
        </td>

        <td>
          <field-toggle
            v-if="!isRevision && !disabled"
            v-model="group.take"
            :column="1"
            :row="groupIndex + 9999"
            :error="getGroupValidationError(groupIndex, 'take')"
            :tooltip-text="tooltipMessage"
            :is-industry-campaign="isIndustryCampaign"
            :disabled="isDisabledInRules(rules.takepayOfferSales.take)"
          />
          <span v-else>{{ group.take }}</span>
        </td>

        <td>
          <field-toggle
            v-if="!isRevision && !disabled"
            v-model="group.pay"
            :column="2"
            :row="groupIndex + 9999"
            :error="getGroupValidationError(groupIndex, 'pay')"
            :tooltip-text="tooltipMessage"
            :is-industry-campaign="isIndustryCampaign"
            :disabled="isDisabledInRules(rules.takepayOfferSales.pay)"
          />
          <span v-else>{{ group.pay }}</span>
        </td>

        <td>
          <field-toggle
            v-if="!isRevision && !disabled"
            v-model="group.maximum_per_client"
            :column="3"
            :row="groupIndex + 9999"
            :tooltip-text="tooltipMessage"
            :is-industry-campaign="isIndustryCampaign"
            :disabled="isDisabledInRules(rules.takepayOfferSales.maximumPerClient)"
          />
          <span v-else-if="isRevision && !group.maximum_per_client" class="empty-value">Vazio</span>
          <span v-else>{{ group.maximum_per_client }}</span>
        </td>

        <td v-if="isIntelligentDistributionEnabled">
          <field-toggle
            v-if="!disabled && !isIntelligentDistributionEditDisabled"
            v-model="group.distribution_limit"
            :error="getGroupValidationError(groupIndex, 'distribution_limit')"
            :column="4"
            :row="groupIndex + 9999"
            :tooltip-text="tooltipMessage"
            :is-industry-campaign="isIndustryCampaign"
            :disabled="disabled || isDisabledInRules(rules.offerSales.active)"
            :numbers-only="true"
          />
          <span v-else-if="isRevision && !group.distribution_limit" class="empty-value">Sem limite</span>
          <span v-else>{{ group && group.distribution_limit ? localeString(group.distribution_limit, 0) : '-' }}</span>
        </td>

        <td></td>
        <td></td>
      </tr>
    </template>

    <!-- table content -->
    <template v-slot:item="{ item, expand, isExpanded, index }">
      <tr :class="{ repeated: addErrorClass(item.sku) }">
        <td>
          <div v-if="isMultipleEans(item)">
            <mf-button type="icon" size="sm" color="marca-1" :icon="isExpanded ? 'indeterminate_check_box' : 'add_box'" @click="expand(!isExpanded)" />
          </div>
        </td>

        <td>
          <div v-if="type === 'grouped' ? group.group_rules_exhibition === 'PRIVATE' : true" class="img-container d-flex align-center justify-center">
            <img v-if="item.image_url && item.image_url.length" :src="item.image_url" alt="" width="100%" />
            <mf-icon v-else>image_not_supported</mf-icon>
          </div>
        </td>

        <td>
          {{ item.sku }}
        </td>

        <td>
          <div class="py-0" :class="helpers.wrongEAN[getIndex(item.sku)] ? 'invalid-input' : ''">
            <span v-if="item.product && item.product.eans && item.product.eans.length > 1">
              {{ getEansCount(item) }}
            </span>
            <span v-else style="word-break: keep-all">
              {{ item.ean }}
            </span>
          </div>
        </td>

        <td>
          <field-toggle
            v-if="!item.products && !disableIfIsGrouped && !isRevision && !disabled"
            v-model="item.name"
            :error="(type === 'ungrouped' && getUngroupedValidationError(item, 'name')) || item.name === ''"
            :text="true"
            :column="0"
            :row="index"
            :tooltip="isDisabledInRules(rules.takepayOfferSales.name)"
            :tooltip-text="tooltipMessage"
            :is-industry-campaign="isIndustryCampaign"
            :disabled="isDisabledInRules(rules.takepayOfferSales.name)"
          />
          <span v-else>{{ item.name }}</span>
        </td>

        <td v-if="type === 'ungrouped'">
          <field-toggle
            v-if="!disableIfIsGrouped && !isRevision && !disabled"
            v-model="item.take"
            :column="1"
            :row="index"
            :error="getUngroupedValidationError(item, 'take')"
            :tooltip-text="tooltipMessage"
            :is-industry-campaign="isIndustryCampaign"
            :disabled="isDisabledInRules(rules.takepayOfferSales.take)"
          />
          <span v-else>{{ item.take }}</span>
        </td>

        <td v-else>
          <span>{{ group.take }}</span>
        </td>

        <td v-if="type === 'ungrouped'">
          <field-toggle
            v-if="!disableIfIsGrouped && !isRevision && !disabled"
            v-model="item.pay"
            :column="2"
            :row="index"
            :error="getUngroupedValidationError(item, 'pay')"
            :tooltip-text="tooltipMessage"
            :is-industry-campaign="isIndustryCampaign"
            :disabled="isDisabledInRules(rules.takepayOfferSales.pay)"
          />
          <span v-else>{{ item.pay }}</span>
        </td>

        <td v-else>
          <span>{{ group.pay }}</span>
        </td>

        <td v-if="type === 'ungrouped'">
          <field-toggle
            v-if="!disableIfIsGrouped && !isRevision && !disabled"
            v-model="item.maximum_per_client"
            :column="3"
            :row="index"
            :tooltip-text="tooltipMessage"
            :is-industry-campaign="isIndustryCampaign"
            :disabled="isDisabledInRules(rules.takepayOfferSales.maximumPerClient)"
          />
          <span v-else-if="isRevision && !item.maximum_per_client" class="empty-value">Vazio</span>
          <span v-else>{{ item.maximum_per_client }}</span>
        </td>

        <td v-else>
          <span v-if="isRevision && !group.maximum_per_client" class="empty-value">Vazio</span>
          <span v-else>{{ group.maximum_per_client || '-' }}</span>
        </td>

        <td v-if="type === 'ungrouped' && isIntelligentDistributionEnabled">
          <field-toggle
            v-if="!disableIfIsGrouped && !isRevision && !disabled && !isIntelligentDistributionEditDisabled"
            v-model="item.distribution_limit"
            :error="getUngroupedValidationError(item, 'distribution_limit')"
            :column="4"
            :row="index"
            :tooltip-text="tooltipMessage"
            :is-industry-campaign="isIndustryCampaign"
            :disabled="disabled || isDisabledInRules(rules.takepayOfferSales.active)"
            :numbers-only="true"
          />
          <span v-else-if="isRevision && !item.distribution_limit" class="empty-value">Sem limite</span>
          <span v-else>{{ item.distribution_limit ? localeString(item.distribution_limit, 0) : '-' }}</span>
        </td>

        <td v-else-if="isIntelligentDistributionEnabled">
          <span v-if="isRevision && group && !group.distribution_limit" class="empty-value">Sem limite</span>
          <span v-else>{{ group && group.distribution_limit ? localeString(group.distribution_limit, 0) : '-' }}</span>
        </td>

        <td v-if="isFranchise && isRevision">
          <div class="d-flex align-center">
            <mf-icon :color="getFranchiseeColor(item.franchisee_eans_frequency_percentage || 0)" size="16" fill class="mr-2" icon="radio_button_unchecked" />
            {{ item.franchisee_eans_frequency_percentage || 0 }}%
          </div>
        </td>

        <td>
          <tooltip :disabled="isTooltipChangingStatusDisabled" :message="tooltipMessage">
            <mf-toggle
              v-model="item.active"
              :disabled="isRevision || isDisabledInRules(rules.takepayOfferSales.active) || disabled"
              class="pa-0 ml-2"
              hide-details
            />
          </tooltip>
        </td>

        <td v-if="!isRevision">
          <tooltip :disabled="!isIndustryCampaign" :message="tooltipMessage">
            <mf-button
              type="icon"
              icon="delete"
              color="cinza-l30"
              :disabled="isRevision || isIndustryCampaign || item.disabled || disabled"
              @click="removeItem(item)"
            />
          </tooltip>
        </td>
      </tr>
    </template>

    <template v-slot:expanded-item="{ headers, item }">
      <td class="expanded-itens" :colspan="headers.length">
        <v-checkbox
          v-for="ean in toSet(item.product.eans)"
          :key="ean"
          v-model="item.eans"
          :value="ean"
          :label="ean"
          :disabled="isCheckboxDisabled(ean, item.eans) || isRevision || disabled"
          color="marca-1"
          multiple
        />
      </td>
    </template>
  </v-data-table>
</template>

<script>
import { offerSalesTables } from '../../../mixins/offer-sales-tables'
import intelligentDistributionMixin from '../../../mixins/intelligent-distribution'
export default {
  name: 'TakePayTable',
  components: {
    FieldToggle: () => import('../../FieldToggle.vue'),
    Tooltip: () => import('../../Tooltip.vue'),
  },
  mixins: [offerSalesTables, intelligentDistributionMixin],
  data: () => ({
    expanded: [],
    singleExpand: true,
  }),
  computed: {
    isTooltipChangingStatusDisabled() {
      return this.isIndustryCampaign ? !this.isDisabledInRules(this.rules.takepayOfferSales.active) : true
    },
  },
  methods: {
    getGroupValidationError(groupIndex, field) {
      return (
        this.validations.groupedProducts &&
        this.validations.groupedProducts.$error &&
        this.validations.groupedProducts.$each &&
        this.validations.groupedProducts.$each[groupIndex] &&
        this.validations.groupedProducts.$each[groupIndex][field] &&
        this.validations.groupedProducts.$each[groupIndex][field].$invalid
      )
    },
    getUngroupedValidationError(item, field) {
      return (
        this.validations.ungroupedProducts &&
        this.validations.ungroupedProducts.$error &&
        this.validations.ungroupedProducts.$each &&
        this.validations.ungroupedProducts.$each[this.getIndex(item.sku)] &&
        this.validations.ungroupedProducts.$each[this.getIndex(item.sku)][field] &&
        this.validations.ungroupedProducts.$each[this.getIndex(item.sku)][field].$invalid
      )
    },
  },
}
</script>

<style lang="scss" scoped>
::v-deep th {
  color: $cinza-d20 !important;
  font-size: 14px;
  font-weight: bold;
}

::v-deep tbody {
  td {
    color: $cinza-0;
    font-size: 14px;
    line-height: 1;
  }
}

.invalid-input {
  color: $vermelho-0;

  .field-padding {
    span {
      border: 1px #ff5252 solid !important;
      padding: 0 5px;
    }
  }
}

.expanded-itens {
  padding-left: 19%;
}

::v-deep .repeated {
  background-color: #ffc4c4;
  &:hover {
    background-color: #ffc4c4 !important;
  }
}

::v-deep th,
::v-deep td {
  padding: 0 8px;
}
.img-container {
  width: 35px;
  height: 35px;

  & img {
    display: block;
    max-width: 100%;
    max-height: 100%;
  }
}
</style>
